import React, { useEffect, useMemo, useState } from 'react';
import './Infobar.css';
import TaskModal from '../Tasks/TaskModal';
import API_BASE_URL from '../../config';

function Infobar({
  record,
  relatedId,
  relatedType,
  onOpenPolicyOrOppModal,
  onEmailClick
}) {
  const [tasks, setTasks] = useState([]);
  const [showTask, setShowTask] = useState(null);

  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTagName, setNewTagName] = useState('');

  const [recordItems, setRecordItems] = useState([]);

  const token = localStorage.getItem('token');
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    if (!relatedId || !relatedType) return;

    fetch(`${API_BASE_URL}/api/tags/${relatedType}/${relatedId}`, { headers })
      .then((res) => res.json())
      .then((data) => setTags(Array.isArray(data) ? data : []))
      .catch((err) => console.error('❌ Error loading tags:', err));
  }, [relatedId, relatedType]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/tags`, { headers })
      .then((res) => res.json())
      .then((data) => setAllTags(Array.isArray(data) ? data : []))
      .catch((err) => console.error('❌ Error loading all tags:', err));
  }, []);

  useEffect(() => {
    if (!relatedId || !relatedType) return;

    fetch(`${API_BASE_URL}/api/tasks?relatedType=${relatedType}&relatedId=${relatedId}`, { headers })
      .then((res) => res.json())
      .then((data) => setTasks(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Error loading tasks in Infobar:', err));
  }, [relatedId, relatedType]);

  useEffect(() => {
    if (!relatedId || !relatedType) return;

    const loadItems = async () => {
      try {
        if (relatedType === 'client') {
          const [policiesRes, oppsRes] = await Promise.all([
            fetch(`${API_BASE_URL}/api/policies/${relatedId}`, { headers }),
            fetch(`${API_BASE_URL}/api/opportunities/client/${relatedId}`, { headers })
          ]);

          const policiesData = await policiesRes.json().catch(() => []);
          const oppsData = await oppsRes.json().catch(() => []);

          const allowedOppStatuses = new Set(['Quoted', 'Pending/Submitted']);

          const combined = [
            ...(Array.isArray(policiesData)
              ? policiesData.map((p) => ({ ...p, type: 'policy' }))
              : []),
            ...(Array.isArray(oppsData)
              ? oppsData
                  .filter((o) => allowedOppStatuses.has(String(o.status || '').trim()))
                  .map((o) => ({ ...o, type: 'opportunity' }))
              : [])
          ];

          setRecordItems(combined);
          return;
        }

        const oppsRes = await fetch(`${API_BASE_URL}/api/opportunities/lead/${relatedId}`, { headers });
        const oppsData = await oppsRes.json().catch(() => []);

        const allowedOppStatuses = new Set(['Quoted', 'Pending/Submitted']);

        setRecordItems(
          Array.isArray(oppsData)
            ? oppsData
                .filter((o) => allowedOppStatuses.has(String(o.status || '').trim()))
                .map((o) => ({ ...o, type: 'opportunity' }))
            : []
        );
      } catch (err) {
        console.error('❌ Error loading policies/opportunities in Infobar:', err);
        setRecordItems([]);
      }
    };

    loadItems();
  }, [relatedId, relatedType]);

  const assignTag = async (tagId) => {
    if (!tagId) return;

    const res = await fetch(`${API_BASE_URL}/api/tags/${relatedType}/${relatedId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ tagId })
    });

    if (res.ok) {
      const updated = await fetch(`${API_BASE_URL}/api/tags/${relatedType}/${relatedId}`, {
        headers
      }).then((r) => r.json());
      setTags(Array.isArray(updated) ? updated : []);
    }
  };

  const removeTag = async (tagId) => {
    const res = await fetch(`${API_BASE_URL}/api/tags/${relatedType}/${relatedId}/${tagId}`, {
      method: 'DELETE',
      headers
    });

    if (res.ok) {
      setTags((prev) => prev.filter((t) => t.id !== tagId));
    }
  };

  const createTag = async () => {
    if (!newTagName.trim()) return;

    const res = await fetch(`${API_BASE_URL}/api/tags`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name: newTagName, color: null })
    });

    if (res.ok) {
      const newTag = await res.json();
      setAllTags((prev) => [...prev, newTag]);
      await assignTag(newTag.id);
      setNewTagName('');
      setShowTagInput(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const clean = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = clean.split('-').map(Number);
    if (!year || !month || !day) return '—';

    const dt = new Date(year, month - 1, day);
    return dt.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === '00:00:00') return '';
    const [hh, mm] = timeStr.split(':');
    if (hh === undefined || mm === undefined) return '';
    const t = new Date();
    t.setHours(Number(hh), Number(mm), 0, 0);
    return t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const locationLine = useMemo(() => {
    const line1 = [record.street_address, record.unit].filter(Boolean).join(' ');
    const line2 = [record.city, record.state, record.zip_code].filter(Boolean).join(', ');
    return { line1, line2 };
  }, [record]);

  const compactItems = recordItems.slice(0, 5);
  const compactTasks = tasks.slice(0, 3);

  return (
    <aside className="infobar">
      <div className="infobar-inner">
        <div className="infobar-header">
          <div>
            <div className="infobar-header-eyebrow">
              {relatedType === 'client' ? 'Quick Access' : 'Lead Snapshot'}
            </div>
            <div className="infobar-header-title">
              Contact, active records, tasks, and tags
            </div>
          </div>
        </div>

        <div className="infobar-section">
          <div className="infobar-section-head">
            <h4>Contact</h4>
          </div>

          <div className="infobar-stack">
            {record.phone && (
              <a className="infobar-contact-row" href={`tel:${record.phone}`}>
                <span className="infobar-row-icon">📞</span>
                <span className="infobar-row-text">{record.phone}</span>
              </a>
            )}

            {record.email && (
              <button
                type="button"
                className="infobar-contact-row infobar-contact-button"
                onClick={() => onEmailClick?.(record.email)}
              >
                <span className="infobar-row-icon">✉️</span>
                <span className="infobar-row-text">{record.email}</span>
              </button>
            )}

            {(locationLine.line1 || locationLine.line2) && (
              <div className="infobar-contact-row static">
                <span className="infobar-row-icon">📍</span>
                <span className="infobar-row-text">
                  {locationLine.line1 && <>{locationLine.line1}<br /></>}
                  {locationLine.line2}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="infobar-section">
          <div className="infobar-section-head">
            <h4>Dates</h4>
          </div>

          <div className="infobar-mini-list">
            {record.dob && (
              <div className="infobar-mini-row">
                <span>DOB</span>
                <strong>{formatDate(record.dob)}</strong>
              </div>
            )}

            {Array.isArray(recordItems) &&
              recordItems
                .filter((item) => item.type === 'policy' && item.effective_date)
                .slice(0, 2)
                .map((item) => (
                  <div className="infobar-mini-row" key={`eff-${item.type}-${item.id}`}>
                    <span>{item.coverageTypeName || 'Policy'} Eff.</span>
                    <strong>{formatDate(item.effective_date)}</strong>
                  </div>
                ))}

            {!record.dob &&
              recordItems.filter((item) => item.type === 'policy' && item.effective_date).length === 0 && (
                <div className="infobar-empty">No key dates</div>
              )}
          </div>
        </div>

        <div className="infobar-section">
          <div className="infobar-section-head">
            <h4>{relatedType === 'client' ? 'Policies & Opportunities' : 'Opportunities'}</h4>
          </div>

          {compactItems.length > 0 ? (
            <div className="infobar-record-list">
              {compactItems.map((item) => (
                <button
                  type="button"
                  key={`${item.type}-${item.id}`}
                  className="infobar-record-card"
                  onClick={() => onOpenPolicyOrOppModal?.(item)}
                >
                  <div className="infobar-record-top">
                    <span className={`infobar-record-badge ${item.type}`}>
                      {item.type === 'policy' ? 'Policy' : 'Opp'}
                    </span>
                    <span className="infobar-record-status">{item.status || 'No Status'}</span>
                  </div>

                  <div className="infobar-record-title">
                    {item.coverageTypeName || item.coverage_type || 'Coverage'}
                  </div>

                  <div className="infobar-record-meta">
                    {item.carrierName || item.carrier || 'No Carrier'}
                  </div>

                  {item.type === 'policy' ? (
                    <div className="infobar-record-detail-grid">
                      <div className="infobar-record-detail">
                        <span className="infobar-record-detail-label">Policy #</span>
                        <span className="infobar-record-detail-value">
                          {item.policy_number || item.policyNumber || '—'}
                        </span>
                      </div>
                      <div className="infobar-record-detail">
                        <span className="infobar-record-detail-label">Effective</span>
                        <span className="infobar-record-detail-value">
                          {formatDate(item.effective_date)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    (item.product_name || item.productName || item.plan || item.plan_name) && (
                      <div className="infobar-record-submeta">
                        {item.product_name || item.productName || item.plan || item.plan_name}
                      </div>
                    )
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="infobar-empty">No active records</div>
          )}
        </div>

        <div className="infobar-section">
          <div className="infobar-section-head">
            <h4>Tasks</h4>
          </div>

          {compactTasks.length > 0 ? (
            <div className="infobar-task-list">
              {compactTasks.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  className="infobar-task-card"
                  onClick={() => setShowTask(t)}
                >
                  <div className="infobar-task-date">
                    {formatDate(t.due_date)} {formatTime(t.start_time)}
                  </div>
                  <div className="infobar-task-title">
                    {t.type} — {t.title}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="infobar-empty">No tasks</div>
          )}
        </div>

        <div className="infobar-section">
          <div className="infobar-section-head">
            <h4>Tags</h4>
          </div>

          <div className="infobar-tag-list">
            {tags.length === 0 && <div className="infobar-empty">No tags</div>}

            {tags.map((tag) => (
              <span key={tag.id} className="infobar-tag-pill">
                <span>{tag.name}</span>
                <button
                  type="button"
                  className="infobar-tag-remove"
                  onClick={() => removeTag(tag.id)}
                  title="Remove tag"
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <select
            className="infobar-tag-select"
            onChange={(e) => assignTag(Number(e.target.value))}
            defaultValue=""
          >
            <option value="">+ Add Tag</option>
            {allTags
              .filter((t) => !tags.some((assigned) => assigned.id === t.id))
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>

          {!showTagInput ? (
            <button
              type="button"
              className="infobar-create-tag-link"
              onClick={() => setShowTagInput(true)}
            >
              + Create Tag
            </button>
          ) : (
            <div className="infobar-tag-create-row">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="New tag name"
              />
              <button type="button" className="infobar-mini-btn primary" onClick={createTag}>
                Add
              </button>
              <button
                type="button"
                className="infobar-mini-btn"
                onClick={() => {
                  setShowTagInput(false);
                  setNewTagName('');
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {showTask && <TaskModal task={showTask} onClose={() => setShowTask(null)} />}
    </aside>
  );
}

export default Infobar;